import { createModalFocusScope } from '../../components/modal-focus-scope.js';

/**
 * Puts the shared confirmation modal in front of a note's delete form.
 *
 * Notes arrive later through the notes stream, which replaces their DOM (and keeps the browse and the
 * search results side by side), so the submit is caught by delegation from the section, never bound
 * per note. Confirming re-submits the pending note's own form, which then goes through as an ordinary
 * POST: JS owns the confirmation step, never the transport. Without JS the modal stays hidden and the
 * note's `Delete` posts directly, exactly as the ranking delete does.
 */
export function initGameDetailsNoteDelete(documentRef = document) {
    const notesSection = documentRef.querySelector('[data-game-notes-section]');
    const modal = documentRef.querySelector('[data-game-note-delete-modal]');
    const dialog = modal instanceof HTMLElement ? modal.querySelector('[data-game-note-delete-dialog]') : null;
    const summary = modal instanceof HTMLElement ? modal.querySelector('[data-game-note-delete-summary]') : null;
    const confirmButton = modal instanceof HTMLElement ? modal.querySelector('[data-game-note-delete-confirm]') : null;
    if (
        !(notesSection instanceof HTMLElement)
        || !(modal instanceof HTMLElement)
        || !(dialog instanceof HTMLElement)
        || !(summary instanceof HTMLElement)
        || !(confirmButton instanceof HTMLElement)
    ) {
        return;
    }

    let pendingForm = null;
    let confirmedForm = null;

    const closeModal = () => {
        if (modal.hidden) {
            return;
        }

        modal.hidden = true;
        pendingForm = null;
        focusScope.deactivate();
    };

    const focusScope = createModalFocusScope({
        dialog,
        requestClose: closeModal,
        documentRef,
    });
    if (focusScope === null) {
        return;
    }

    notesSection.addEventListener('submit', (event) => {
        const form = event.target;
        if (
            !(form instanceof HTMLFormElement)
            || !form.matches('[data-game-note-delete-form]')
            || form === confirmedForm
        ) {
            return;
        }

        event.preventDefault();
        if (!modal.hidden) {
            return;
        }

        pendingForm = form;
        summary.textContent = attachmentSummary(Number.parseInt(form.dataset.attachmentCount ?? '0', 10));
        modal.hidden = false;
        focusScope.activate(event.submitter ?? form.querySelector('button[type="submit"]'));
        focusScope.focus();
    });

    confirmButton.addEventListener('click', () => {
        const form = pendingForm;
        // The stream may have replaced the note while the modal was open; there is nothing left to confirm.
        if (form === null || !form.isConnected) {
            closeModal();

            return;
        }

        confirmedForm = form;
        form.requestSubmit();
    });

    modal.querySelectorAll('[data-game-note-delete-close]').forEach((button) => {
        button.addEventListener('click', () => closeModal());
    });
}

function attachmentSummary(count) {
    if (!Number.isFinite(count) || count <= 0) {
        return 'The note will be permanently deleted.';
    }

    if (count === 1) {
        return 'The note and its 1 attachment will be permanently deleted.';
    }

    return `The note and its ${count} attachments will be permanently deleted.`;
}
